import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, ChevronLeft } from 'lucide-react';

/**
 * PaymentMethodPicker — donor-facing payment-method chooser for the PUBLIC
 * campaign page. Rendered only when the campaign opts in
 * (campaign.showPaymentMethodPicker); otherwise the page keeps its historical
 * "straight to the Tranzila iframe" flow untouched.
 *
 * Order (per design): bit → credit card → Apple Pay → Google Pay.
 *
 * Method visibility:
 *   - Credit card — always shown (opens the existing iframe flow via onSelectCredit).
 *   - bit         — enableBit && bitAvailable (org has GamaPay) && currency === 'ILS'.
 *   - Apple Pay   — enableApplePay && ApplePaySession.canMakePayments(). That feature
 *                   check already encodes the device rules: Mac+Safari ✓, Mac+other ✗,
 *                   iPhone (Safari/Chrome) ✓, Windows/Android ✗.
 *   - Google Pay  — enableGooglePay && (Windows || Android) only — never on Apple devices.
 *
 * Apple/Google charge natively through Tranzila Hosted Fields. The thtk +
 * charge params come from GET /public/wallet/charge-params (passed in as
 * requestWalletCharge); those params carry notify_url + order_id so a
 * successful wallet charge creates a real Donation via the same
 * /payments/tranzila/callback the iframe uses.
 */

interface WalletChargeResponse {
  ok: boolean;
  thtk?: string;
  terminalName?: string;
  chargeParams?: Record<string, string>;
  note?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  amount: number;
  currency: string;
  campaignName?: string;
  enableBit: boolean;
  enableGooglePay: boolean;
  enableApplePay: boolean;
  bitAvailable: boolean;
  onSelectCredit: () => void;
  onSelectBit: () => void;
  requestWalletCharge: (method: 'google' | 'apple') => Promise<WalletChargeResponse>;
  onWalletSuccess: (payload: Record<string, string>) => void;
  onWalletError: (msg: string) => void;
}

// ── Brand logos (match the admin debug page) ────────────────────────────────
const GoogleG = () => (
  <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
const AppleLogo = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff" aria-hidden>
    <path d="M17.05 12.04c-.03-2.77 2.26-4.1 2.36-4.16-1.29-1.88-3.29-2.14-4-2.17-1.7-.17-3.32 1-4.18 1-.86 0-2.19-.98-3.6-.95-1.85.03-3.56 1.08-4.51 2.73-1.93 3.35-.49 8.31 1.38 11.03.92 1.33 2.01 2.82 3.44 2.77 1.38-.06 1.9-.89 3.57-.89 1.66 0 2.13.89 3.59.86 1.48-.03 2.42-1.36 3.33-2.7 1.05-1.55 1.48-3.05 1.5-3.13-.03-.01-2.88-1.1-2.91-4.37zM14.28 4.13c.76-.92 1.27-2.2 1.13-3.47-1.09.04-2.42.73-3.2 1.65-.7.81-1.31 2.11-1.15 3.36 1.22.09 2.46-.62 3.22-1.54z" />
  </svg>
);
const CardLogo = () => (
  <svg viewBox="0 0 40 28" width="30" height="21" aria-hidden>
    <rect x="0.5" y="0.5" width="39" height="27" rx="4" fill="url(#pmpcardg)" />
    <rect x="0" y="6" width="40" height="5" fill="#1f2937" opacity="0.55" />
    <circle cx="24" cy="20" r="6" fill="#EB001B" opacity="0.9" />
    <circle cx="31" cy="20" r="6" fill="#F79E1B" opacity="0.85" />
    <defs>
      <linearGradient id="pmpcardg" x1="0" y1="0" x2="40" y2="28" gradientUnits="userSpaceOnUse">
        <stop stopColor="#6366F1" /><stop offset="1" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
  </svg>
);

const loadScriptCache: Record<string, Promise<void> | undefined> = {};
function loadScript(src: string): Promise<void> {
  const ex = loadScriptCache[src];
  if (ex) return ex;
  const p = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = () => resolve(); s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
  loadScriptCache[src] = p;
  return p;
}

// ── Device detection (matches the agreed rules) ─────────────────────────────
function detectDevice() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isWindows = /Windows/.test(ua);
  let appleCapable = false;
  try { appleCapable = !!(window as any).ApplePaySession && (window as any).ApplePaySession.canMakePayments(); } catch { appleCapable = false; }
  return { isIOS, isAndroid, isWindows, appleCapable };
}

const PaymentMethodPicker: React.FC<Props> = ({
  open, onClose, amount, currency, campaignName,
  enableBit, enableGooglePay, enableApplePay, bitAvailable,
  onSelectCredit, onSelectBit, requestWalletCharge, onWalletSuccess, onWalletError,
}) => {
  const fieldsRef = useRef<any>(null);
  const sdkReadyRef = useRef(false);
  const [busy, setBusy] = useState<null | 'google' | 'apple'>(null);
  const [show, setShow] = useState({ apple: false, google: false });

  const showBit = enableBit && bitAvailable && currency === 'ILS';

  // Decide wallet visibility from the donor's device once the dialog opens.
  useEffect(() => {
    if (!open) return;
    const d = detectDevice();
    setShow({
      apple: enableApplePay && d.appleCapable,
      google: enableGooglePay && (d.isWindows || d.isAndroid),
    });
  }, [open, enableApplePay, enableGooglePay]);

  // Pre-load + initialise the Hosted Fields SDK while the donor reads the
  // dialog, so a wallet tap charges without an init delay. Off-screen card
  // fields are required by the SDK even though wallets don't use them.
  useEffect(() => {
    if (!open) return;
    if (!show.apple && !show.google) return;
    let cancelled = false;
    (async () => {
      try {
        await loadScript('https://hf.tranzila.com/assets/js/thostedf.js');
        if (show.apple) await loadScript('https://hf.tranzila.com/assets/js/thfapplepay.js');
        if (cancelled) return;
        const Tzla = (window as any).TzlaHostedFields;
        if (!Tzla?.create || fieldsRef.current) return;
        const fields = Tzla.create({
          sandbox: false,
          fields: {
            credit_card_number: { selector: '#pmp_cc', placeholder: '' },
            cvv: { selector: '#pmp_cvv', placeholder: '' },
            expiry: { selector: '#pmp_exp', placeholder: '', version: '1' },
          },
        });
        fieldsRef.current = fields;
        fields.onEvent?.('ready', () => { sdkReadyRef.current = true; });
      } catch { /* wallet buttons will surface the error on tap */ }
    })();
    return () => { cancelled = true; };
  }, [open, show.apple, show.google]);

  if (!open) return null;

  const chargeCb = (method: 'google' | 'apple') => (err: any, response: any) => {
    setBusy(null);
    if (err) {
      const msg = (err && (err.messages?.[0]?.message || err.message)) || '';
      // User-cancelled wallet sheets are not real errors — just reopen the picker.
      if (/cancel/i.test(String(msg) + JSON.stringify(err || {}))) return;
      onWalletError(msg || 'התשלום לא הושלם');
      return;
    }
    // Success — the Donation is created server-side via notify_url. Hand off to
    // the parent's success handler (redirect to the thank-you page).
    onWalletSuccess({
      method,
      ...(response?.transaction_response?.transaction_id ? { transactionId: String(response.transaction_response.transaction_id) } : {}),
    });
  };

  const payWallet = async (method: 'google' | 'apple') => {
    if (busy) return;
    setBusy(method);
    try {
      const res = await requestWalletCharge(method);
      if (!res.ok || !res.thtk || !res.chargeParams) {
        setBusy(null);
        onWalletError('אמצעי התשלום אינו זמין כרגע. נסו כרטיס אשראי.');
        return;
      }
      const params = { ...res.chargeParams, thtk: res.thtk };
      if (method === 'google') {
        const fn = fieldsRef.current?.chargeGpay;
        if (typeof fn !== 'function') { setBusy(null); onWalletError('Google Pay אינו זמין'); return; }
        fieldsRef.current.chargeGpay(params, chargeCb('google'));
      } else {
        const fn = (window as any).hfChargeApple;
        if (typeof fn !== 'function') { setBusy(null); onWalletError('Apple Pay אינו זמין'); return; }
        fn({ ...params, labelApple: 'Donation', supported_network: ['visa', 'masterCard', 'amex', 'discover'] }, chargeCb('apple'));
      }
    } catch (e: any) {
      setBusy(null);
      onWalletError(e?.message || 'שגיאה בתשלום');
    }
  };

  const amountLabel = `₪${Number(amount).toLocaleString('he-IL')}`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} dir="rtl">
      {/* Off-screen card fields the SDK needs to initialise (wallets don't use them). */}
      <div style={{ position: 'absolute', left: -9999, top: 0, height: 0, overflow: 'hidden' }}>
        <div id="pmp_cc" /><div id="pmp_cvv" /><div id="pmp_exp" />
      </div>

      <div style={{ width: 360, maxWidth: '100%', background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #f1f1f1' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>בחירת אמצעי תשלום</div>
          <button onClick={onClose} aria-label="סגור" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}><X size={20} /></button>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#111' }}>{amountLabel}</div>
            {campaignName && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{campaignName}</div>}
          </div>

          {showBit && (
            <button onClick={() => { onClose(); onSelectBit(); }}
              style={{ width: '100%', height: 60, background: '#16383C', border: 'none', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
              <span style={{ fontFamily: "'Varela Round', sans-serif", color: '#57DEDF', fontWeight: 700, fontSize: 24, letterSpacing: '-0.5px' }}>bit</span>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#eafcfc' }}>תשלום מהיר ב-bit</span>
            </button>
          )}

          <button onClick={() => { onClose(); onSelectCredit(); }}
            style={{ width: '100%', height: 52, background: '#fff', border: '1px solid #d1d5db', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', cursor: 'pointer', marginBottom: show.apple || show.google ? 10 : 0 }}>
            <CardLogo />
            <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>כרטיס אשראי</span>
            <ChevronLeft size={18} color="#9ca3af" style={{ marginInlineStart: 'auto' }} />
          </button>

          {(show.apple || show.google) && (
            <div style={{ display: 'flex', gap: 8 }}>
              {show.apple && (
                <button onClick={() => payWallet('apple')} disabled={!!busy}
                  style={{ flex: 1, height: 52, background: '#000', border: 'none', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: busy ? 'wait' : 'pointer', opacity: busy && busy !== 'apple' ? 0.5 : 1 }}>
                  {busy === 'apple' ? <Loader2 size={18} color="#fff" className="animate-spin" /> : <><AppleLogo /><span style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Pay</span></>}
                </button>
              )}
              {show.google && (
                <button onClick={() => payWallet('google')} disabled={!!busy}
                  style={{ flex: 1, height: 52, background: '#000', border: 'none', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: busy ? 'wait' : 'pointer', opacity: busy && busy !== 'google' ? 0.5 : 1 }}>
                  {busy === 'google' ? <Loader2 size={18} color="#fff" className="animate-spin" /> : <><GoogleG /><span style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Pay</span></>}
                </button>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 16, color: '#9ca3af', fontSize: 12 }}>
            תשלום מאובטח · Tranzila
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodPicker;
